/**
 * Deep thread search — client caller for the `threadSearch` Cloud Function
 * (chat-thread experience PR-5, 2026-09-09). Searches the caller's OWN
 * threads on the server (own per-doc + legacy messages, and the shared store
 * floored at the caller's join point) — the "search all messages" tier
 * behind the instant, browser-held one. Fail-closed: any error resolves to
 * `{ ok: false, hits: [] }`; never throws.
 */
import { getAuth } from 'firebase/auth';

export interface DeepThreadSearchHit {
  threadId: string;
  /** The thread's title as the server knows it (the index doc's). */
  title: string;
  kind: 'thread' | 'former-title' | 'message';
  snippet: string;
  score: number;
  messageId?: string;
  orderTs?: number;
  isUser?: boolean;
  senderId?: string;
}

export interface DeepThreadSearchResult {
  ok: boolean;
  hits: DeepThreadSearchHit[];
}

export function threadSearchUrl(): string {
  const override = (process.env.REACT_APP_FUNCTIONS_BASE || '').trim();
  const base = override
    ? override.replace(/\/$/, '')
    : `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3'}.cloudfunctions.net`;
  return `${base}/threadSearch`;
}

export function normalizeDeepHits(raw: unknown): DeepThreadSearchHit[] {
  const list = Array.isArray(raw) ? raw : [];
  const out: DeepThreadSearchHit[] = [];
  for (const r of list as Array<Record<string, unknown>>) {
    const threadId = typeof r?.threadId === 'string' ? r.threadId.trim() : '';
    if (!threadId) continue;
    const kind = r?.kind === 'message' || r?.kind === 'former-title' ? r.kind : 'thread';
    const score = Number(r?.score);
    out.push({
      threadId,
      title: typeof r?.title === 'string' ? r.title.slice(0, 200) : '',
      kind,
      snippet: typeof r?.snippet === 'string' ? r.snippet.slice(0, 400) : '',
      score: Number.isFinite(score) ? score : 0,
      ...(typeof r?.messageId === 'string' && r.messageId ? { messageId: r.messageId } : {}),
      ...(Number.isFinite(Number(r?.orderTs)) && Number(r?.orderTs) > 0 ? { orderTs: Number(r.orderTs) } : {}),
      ...(typeof r?.isUser === 'boolean' ? { isUser: r.isUser } : {}),
      ...(typeof r?.senderId === 'string' && r.senderId ? { senderId: r.senderId } : {}),
    });
  }
  return out;
}

export async function deepSearchThreads(
  query: string,
  options: { limit?: number; signal?: AbortSignal } = {},
): Promise<DeepThreadSearchResult> {
  const q = (query || '').trim().slice(0, 200);
  if (q.length < 2) return { ok: true, hits: [] };
  const user = getAuth().currentUser;
  if (!user) return { ok: false, hits: [] };
  try {
    const token = await user.getIdToken();
    const res = await fetch(threadSearchUrl(), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ q, limit: options.limit ?? 30 }),
      signal: options.signal,
    });
    if (!res.ok) return { ok: false, hits: [] };
    const body = (await res.json().catch(() => null)) as { success?: boolean; hits?: unknown } | null;
    if (!body || body.success !== true) return { ok: false, hits: [] };
    return { ok: true, hits: normalizeDeepHits(body.hits) };
  } catch {
    return { ok: false, hits: [] };
  }
}
